import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Target, Plus } from 'lucide-react';

const ProjectObjectivesCard = ({ project, onOpenGoalsModal }) => {
  const { user } = useAuth();
  const allObjectives = project?.objectives || [];
  const isEndlessMode = project?.gameMode === 'endless';

  // Filter out completed objectives - only show unfinished ones
  const objectives = allObjectives.filter((objective) => {
    // Explicitly check if marked as completed
    if (objective.isCompleted === true) {
      return false;
    }
    
    const isOpenEndedObjective = objective.isOpenEnded || isEndlessMode;
    // Open-ended objectives are never "completed" in the traditional sense
    if (isOpenEndedObjective) {
      return true;
    }
    
    // For regular objectives, also check if progress meets target
    const progressCount = objective.progressCount || 0;
    const targetCount = objective.targetCount || 1;
    if (progressCount >= targetCount) {
      return false;
    }
    
    return true;
  });

  const hasObjectives = objectives.length > 0;

  // Don't show for finished projects
  if (project?.status !== 'ongoing') {
    return null;
  }

  // Only owners can add/browse objectives, so hide for everyone else
  const isOwner = user && project && user.id === project.ownerId;
  if (!isOwner) {
    return null;
  }

  return (
    <div className="mb-5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl overflow-hidden cursor-pointer hover:border-gray-300 dark:hover:border-gray-600 transition-colors" onClick={onOpenGoalsModal}>
      {!hasObjectives ? (
        // Setup state - no objectives yet
        <div className="flex min-h-[56px]">
          {/* Icon stripe */}
          <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 w-full h-full bg-amber-500 dark:bg-amber-600 rounded-l-2xl flex items-center justify-center">
              <Target size={20} className="text-white" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 p-5 flex items-center min-w-0">
            <div className="flex items-center justify-between w-full gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold text-gray-900 dark:text-white">
                  Add Objectives
                </div>
                <div className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">
                  Define what you want to achieve
                </div>
              </div>

              <button
                onClick={onOpenGoalsModal}
                className="flex items-center gap-1.5 px-3 py-2 bg-amber-500 hover:bg-amber-600 text-white text-[13px] font-semibold rounded-lg transition-colors flex-shrink-0"
              >
                <Plus className="w-4 h-4" />
                <span className="hidden sm:inline">Add</span>
              </button>
            </div>
          </div>
        </div>
      ) : (
        // Has objectives state
        <div className="flex min-h-[56px]">
          {/* Icon stripe */}
          <div className="relative w-16 min-h-full overflow-hidden flex-shrink-0">
            <div className="absolute inset-0 w-full h-full bg-green-500 dark:bg-green-600 rounded-l-2xl flex items-center justify-center">
              <Target size={20} className="text-white" />
            </div>
          </div>
          
          {/* Content */}
          <div className="flex-1 py-5 px-5 flex flex-col justify-center min-w-0">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                {/* Title with count */}
                <div className="flex items-center gap-2">
                  <span className="text-[14px] font-semibold text-gray-900 dark:text-white">
                    Objectives
                  </span>
                  <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                    {objectives.length}
                  </span>
                </div>
                
                {/* Objectives list - cleaner bullets */}
                <div className="space-y-1 mt-2">
                  {objectives.slice(0, 3).map((objective, index) => {
                    const isOpenEndedObjective = objective.isOpenEnded || isEndlessMode;
                    const progressCount = objective.progressCount || 0;
                    const targetCount = objective.targetCount || 1;
                    
                    return (
                      <div key={objective.id || index} className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                          progressCount > 0 ? 'bg-amber-500' : 'bg-gray-300 dark:bg-gray-600'
                        }`} />
                        <span className="text-[12px] truncate text-gray-700 dark:text-gray-300">
                          {objective.objectiveName}
                        </span>
                        {!isOpenEndedObjective && progressCount > 0 && (
                          <span className="text-[10px] text-gray-400 dark:text-gray-500 flex-shrink-0">
                            {progressCount}/{targetCount}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  
                  {objectives.length > 3 && (
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 pl-3.5">
                      +{objectives.length - 3} more
                    </div>
                  )}
                </div>
              </div>

              <button
                onClick={onOpenGoalsModal}
                className="flex items-center gap-1.5 px-3 py-2 bg-green-500 hover:bg-green-600 text-white text-[13px] font-semibold rounded-lg transition-colors flex-shrink-0"
              >
                View
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectObjectivesCard;
